import { Box, Button, Column, Table } from '@palmetto/palmetto-components';
import { Icon } from '@palmetto/palmetto-components';

export interface BulkModifyAccountsResultsData {
  total: number;
  successCount: number;
  failedCount: number;
  successful: string[];
  failed: { accountId: string; error: string }[];
}

interface BulkModifyAccountsResultsProps {
  results: BulkModifyAccountsResultsData;
  closeResults: () => void;
}

interface StatusResultProps {
  icon: 'c-check' | 't-warning' | 'remove';
  color: 'success' | 'danger' | 'warning-500';
  text: string;
  accountsTable?: React.ReactNode;
}

const StatusResult = ({ icon, color, text, accountsTable }: StatusResultProps) => (
  <Box direction="row">
    <Box>
      <Icon name={icon} color={color} size="1xl" />
    </Box>
    <Box display="block" width="100" padding="xs 0 0 md" childGap="md">
      <Box as="h3" fontWeight="bold">
        {text}
      </Box>
      {accountsTable}
    </Box>
  </Box>
);

const normalizeAccountLabel = (length: number) => `${length} ${length === 1 ? 'account' : 'accounts'}`;

const successfulAccountsTableColumnConfig: Column[] = [{ heading: 'ACCOUNT ID', dataKey: 'accountId' }];

const failedAccountsTableColumnConfig: Column[] = [
  { heading: 'ACCOUNT ID', dataKey: 'accountId' },
  { heading: 'ERROR', dataKey: 'error' },
];

export function BulkModifyAccountsResults({ results, closeResults }: BulkModifyAccountsResultsProps) {
  const successfulRows = results.successful.map((accountId) => ({ accountId }));

  return (
    <Box childGap={'xl'} padding={{ base: 'sm md', desktop: '0 0', tablet: '0 0' }}>
      {results.successCount > 0 && (
        <Box>
          <StatusResult
            icon="c-check"
            color="success"
            text={`${normalizeAccountLabel(results.successCount)} processed successfully`}
            accountsTable={
              <Table
                isScrollable={{
                  x: true,
                }}
                rowKey="accountId"
                columns={successfulAccountsTableColumnConfig}
                rows={successfulRows}
              />
            }
          />
        </Box>
      )}
      {results.failedCount > 0 && (
        <Box>
          <StatusResult
            icon="remove"
            color="danger"
            text={`${normalizeAccountLabel(results.failedCount)} failed`}
            accountsTable={
              <Table
                isScrollable={{
                  x: true,
                }}
                rowKey="accountId"
                columns={failedAccountsTableColumnConfig}
                rows={results.failed}
              />
            }
          />
        </Box>
      )}
      <Box
        direction={{
          base: 'column',
          tablet: 'row',
        }}
        alignItems={{
          base: 'stretch',
          tablet: 'flex-end',
        }}
        justifyContent={{
          tablet: 'flex-end',
        }}
        childGap="sm"
        style={{ flexShrink: 0 }}
      >
        <Button size="md" variant="secondary" tone="neutral" aria-label="close results" onClick={closeResults}>
          Close
        </Button>
      </Box>
    </Box>
  );
}
